import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { type PlaceKind } from '@bandido/shared/types';

import { CONFIG } from '@frontend/config';
import { countries } from '@frontend/features/common/constants/countries';
import { committedParty } from '../committedBookingContext';
import { useSearchCarRatePlans, useSearchCars } from '../queries';
import {
  UrlParams,
  cleanParams,
  enumParam,
  listParam,
  numParam,
  serializeList,
  isIncompleteSearch,
  useSearchUrlState,
} from '../useSearchUrlState';
import {
  CarBody,
  CarFilters,
  CarOffer,
  CarSort,
  EMPTY_CAR_FILTERS,
  carFacetCount,
  carFiltersForRatePlanComparison,
  carRatePlanComparisonGenerationKey,
  carRatePlanComparisonPayload,
  carRatePlanNames,
  carRatePlanComparisonState,
  carDropOffReferenceStatus,
  carPickUpUnsearchableReason,
  carPlaceFromParams,
  carPlaceSearchParams,
  carPickUpReferenceStatus,
  carSearchPayloadFromParams,
  carRouteMapPoints,
  filterCars,
  groupCarOffers,
  isCarRatePlanProvider,
  mergeCarRatePlanComparison,
  carLocationUrlParams,
  rentalDays,
  shortLocationName,
  sortCarGroups,
} from '../cars';
import { PlaceRef } from '../places';
import {
  FilterChip,
  FilterGroup,
  ResultFiltersBar,
} from './ResultFiltersBar';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { PlaceAutocomplete } from './PlaceAutocomplete';
import { describeCars } from '../describeSearch';
import { SearchBar, SearchDivider, SearchField } from './SearchBar';
import {
  SEARCH_DATE_FIELD_WIDTH,
  SEARCH_FIELD_HALF_SHARE,
  SEARCH_FIELD_NARROW_SHARE,
  SEARCH_FIELD_WIDE_SHARE,
} from '../searchFieldLayout';
import { RecentSearches } from './RecentSearches';
import { CarOfferCard } from './CarOfferCard';
import { BookingModal } from './BookingModal';
import { quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import { useFareCountdown } from '../useFareCountdown';
import {
  useSearchResultAuthority,
  useSearchSelectionBlockedReason,
} from '../useSearchResultAuthority';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import { searchRouteMapKey } from '../routeMap';
import { SearchRouteMap } from './SearchRouteMap';
import {
  latestSearchDate,
  reconcileSearchEndDate,
  todaySearchDate,
} from '../searchDates';
import { SearchDatePicker } from './SearchDatePicker';
import { defaultAttemptIdFactory } from '../holdAttemptIdentity';

/** El país de residencia se repite en cada búsqueda del mismo asesor. */
const RESIDENCE_KEY = 'travelServices.cars.residence';

const BODY_FILTERS: CarBody[] = ['car', 'suv', 'van', 'pickup', 'convertible'];
const PAX_FILTERS = [4, 5, 7];

/**
 * Kinds del picker de PLACES para autos: solo ciudad/aeropuerto — un país o
 * una región no tienen `country` propio (`Place.country` ausente ahí), y
 * `country` es REQUERIDO para poder buscar (ver `canSearch`). Mismo alcance
 * necesario para el pedido contractual de Cars.
 */
const CAR_PLACE_KINDS: PlaceKind[] = ['city', 'airport'];

interface FilterGroupsProps {
  filters: CarFilters;
  setFilters: Dispatch<SetStateAction<CarFilters>>;
  companies: string[];
  rateNames: string[];
  /** Ofertas SIN filtrar: son la base para contar cada opción. */
  offers: CarOffer[];
  /** Mobile: cada grupo en su propia fila, con más aire para el dedo. */
  stacked?: boolean;
}

/**
 * Los grupos de filtros, compartidos por la barra fija de desktop y el panel
 * de mobile: mismas opciones y mismo estado, distinta disposición.
 */
function FilterGroups({ filters, setFilters, companies, rateNames, offers, stacked }: FilterGroupsProps) {
  const { t } = useTranslation();
  const countWith = (patch: Partial<CarFilters>) =>
    carFacetCount(offers, filters, patch);

  const toggleInList = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  return (
    <>
      <FilterGroup label={t('travelServices.cars.typeFilter')} stacked={stacked}>
        {BODY_FILTERS.map((body) => (
          <FilterChip
            key={body}
            label={t(`travelServices.cars.bodies.${body}`)}
            count={countWith({ bodies: [body] })}
            active={filters.bodies.includes(body)}
            onClick={() =>
              setFilters((prev) => ({ ...prev, bodies: toggleInList(prev.bodies, body) }))
            }
          />
        ))}
      </FilterGroup>
      <FilterGroup label={t('travelServices.cars.paxFilter')} stacked={stacked}>
        {PAX_FILTERS.map((pax) => (
          <FilterChip
            key={pax}
            label={`${pax}+`}
            count={countWith({ minPassengers: pax })}
            active={filters.minPassengers === pax}
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                minPassengers: prev.minPassengers === pax ? 0 : pax,
              }))
            }
          />
        ))}
      </FilterGroup>
      <FilterGroup label={t('travelServices.cars.companyFilter')} stacked={stacked}>
        {companies.map((company) => (
          <FilterChip
            key={company}
            label={company}
            count={countWith({ companies: [company] })}
            active={filters.companies.includes(company)}
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                companies: toggleInList(prev.companies, company),
              }))
            }
          />
        ))}
      </FilterGroup>
      {rateNames.length > 0 && (
        <FilterGroup label={t('travelServices.cars.protectionFilter')} stacked={stacked}>
          {rateNames.map((rateName) => (
            <FilterChip
              key={rateName}
              label={rateName}
              count={countWith({ rateNames: [rateName] })}
              active={filters.rateNames.includes(rateName)}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  rateNames: toggleInList(prev.rateNames, rateName),
                }))
              }
            />
          ))}
        </FilterGroup>
      )}
      <Box
        display="flex"
        flexDirection={stacked ? 'column' : 'row'}
        alignItems={stacked ? 'flex-start' : 'center'}
        width={stacked ? '100%' : undefined}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filters.automaticOnly}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, automaticOnly: event.target.checked }))
              }
            />
          }
          label={
            <Typography variant="body2">{t('travelServices.cars.automaticOnly')}</Typography>
          }
        />
        <Tooltip title={t('travelServices.cars.onTerminalTooltip')}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.onTerminalOnly}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, onTerminalOnly: event.target.checked }))
                }
              />
            }
            label={
              <Typography variant="body2">
                {t('travelServices.cars.onTerminalOnly')}
              </Typography>
            }
          />
        </Tooltip>
      </Box>
    </>
  );
}

/**
 * Buscador de autos. Barra horizontal con un solo punto por defecto (retiro =
 * devolución, como en los portales del rubro) y devolución en otro lugar
 * opt-in. El `country` NO se le pregunta al asesor: sale del país de la
 * ubicación de retiro. El país de RESIDENCIA sí, porque cambia la tarifa, y
 * queda recordado.
 *
 * El proveedor devuelve muchas ofertas (100+ para un aeropuerto grande), así
 * que el refinado es CLIENT-SIDE sobre lo ya traído: filtrar no vuelve al hub.
 */
/**
 * El payload a partir de la URL. `null` = la URL no describe una búsqueda
 * ejecutable (sin `buscar=1` o incompleta) y la query queda deshabilitada.
 */
export function CarsSearchForm() {
  const { t, i18n } = useTranslation();
  const url = useSearchUrlState({ vertical: 'cars', dateParam: 'pickUpDate' });
  const searchPayload = useMemo(
    () => carSearchPayloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchCars(searchPayload);
  const invalidCommittedDropOff =
    url.params.buscar === '1' && carDropOffReferenceStatus(url.params) === 'invalid';
  const invalidCommittedPickUp =
    url.params.buscar === '1' && carPickUpReferenceStatus(url.params) === 'invalid';
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload ? quoteSearchIdentity('cars', CONFIG.AGENCY_ID, searchPayload) : undefined;
  const [comparisonRequest, setComparisonRequest] = useState<{
    baseIdentity: string;
    baseUpdatedAt: number;
    requestId: string;
    payload: NonNullable<typeof searchPayload>;
  } | null>(null);
  const comparisonIsForActiveSearch = Boolean(
    comparisonRequest &&
      comparisonRequest.baseIdentity === activeSearchIdentity &&
      comparisonRequest.baseUpdatedAt === search.dataUpdatedAt,
  );
  const comparisonPayload = comparisonIsForActiveSearch
    ? comparisonRequest?.payload ?? null
    : null;
  const comparison = useSearchCarRatePlans(
    comparisonPayload,
    carRatePlanComparisonGenerationKey(
      comparisonRequest?.baseUpdatedAt ?? 0,
      comparisonRequest?.requestId ?? '',
    ),
  );

  // El formulario arranca de la URL: un deep link (o volver al vertical) lo
  // deja tal cual estaba.
  const [pickUpPlace, setPickUpPlace] = useState<PlaceRef | null>(() =>
    carPlaceFromParams(url.params, 'pickUp'));
  const [dropOffPlace, setDropOffPlace] = useState<PlaceRef | null>(() =>
    carPlaceFromParams(url.params, 'dropOff'));
  const [differentDropOff, setDifferentDropOff] = useState(
    () => Boolean(url.params.dropOffCode || url.params.dropOffLat)
  );
  const [pickUpDate, setPickUpDate] = useState(() => url.params.pickUpDate ?? '');
  const [pickUpTime, setPickUpTime] = useState(() => url.params.pickUpTime ?? '10:00');
  const [dropOffDate, setDropOffDate] = useState(() => url.params.dropOffDate ?? '');
  const [dropOffDateOpen, setDropOffDateOpen] = useState(false);
  const [dropOffTime, setDropOffTime] = useState(() => url.params.dropOffTime ?? '10:00');
  // La URL le gana al localStorage: un link compartido tiene que reproducirse
  // igual en cualquier máquina.
  const [residence, setResidence] = useState(
    () => url.params.residencia ?? localStorage.getItem(RESIDENCE_KEY) ?? '',
  );
  // Prepago por default: es la modalidad que se cobra en la agencia.
  const [paymentType, setPaymentType] = useState<'' | 'ppd' | 'pod'>(() =>
    enumParam(url.params, 'pago', ['', 'ppd', 'pod'] as const, 'ppd'));
  const [filters, setFilters] = useState<CarFilters>(() => ({
    ...EMPTY_CAR_FILTERS,
    bodies: listParam(url.params, 'fTipo') as CarFilters['bodies'],
    companies: listParam(url.params, 'fCompania'),
    minPassengers: numParam(url.params, 'fPax', 0),
    automaticOnly: url.params.fAutomatico === '1',
    onTerminalOnly: url.params.fTerminal === '1',
  }));
  const [sort, setSort] = useState<CarSort>(() =>
    enumParam(url.params, 'orden', ['price', 'passengers', 'company'] as const, 'price'));

  // Los refinamientos viajan en la URL (sin tocar el historial): un link
  // compartido muestra la misma vista filtrada.
  useEffect(() => {
    url.update({
      fTipo: serializeList(filters.bodies),
      fCompania: serializeList(filters.companies),
      fPax: filters.minPassengers ? String(filters.minPassengers) : undefined,
      fAutomatico: filters.automaticOnly ? '1' : undefined,
      fTerminal: filters.onTerminalOnly ? '1' : undefined,
      orden: sort === 'price' ? undefined : sort,
    });
  }, [filters, sort, url]);
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<CarOffer> | null
  >(null);
  const bookingOffer = bookingQuote?.offer ?? null;
  /**
   * La búsqueda que produjo lo que está en pantalla sale de la URL.
   * `pickUpLocation` es solo para MOSTRAR el nombre en el resumen de
   * reserva — mismo fallback que `describeCars` (`Nombre ?? Code`), vale
   * para los dos modos sin reconstruir el punto completo (acá alcanza el
   * texto, no hace falta el id tipado).
   */
  const searched = searchPayload
    ? {
      pickUpDate: searchPayload.pickUpDate,
      pickUpTime: searchPayload.pickUpTime,
      dropOffDate: searchPayload.dropOffDate,
      dropOffTime: searchPayload.dropOffTime,
      pickUpLocation: { name: url.params.pickUpNombre ?? url.params.pickUpCode ?? '' },
    }
    : null;

  /**
   * Los lugares del pedido para el mapa: de los parámetros COMMITEADOS, igual
   * que `searched` — no del formulario vivo.
   */
  const bookingRoutePoints = searchPayload ? carRouteMapPoints(url.params) : [];

  const {language} = i18n;

  const residenceOptions = useMemo(() => {
    const displayNames = (() => {
      try {
        return new Intl.DisplayNames([language], { type: 'region' });
      } catch {
        return null;
      }
    })();
    return countries
      .map((country) => ({
        code: country.code,
        name: displayNames?.of(country.code) ?? country.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [language]);

  // El país sale del lugar elegido (`place.country.code`, viene de ubikeishon —
  // bandido lo sigue mandando él mismo, rutero no lo deriva); sin él el
  // proveedor no puede tarifar.
  const pickUpName = pickUpPlace?.name;
  const dropOffName = dropOffPlace?.name;
  const country = pickUpPlace?.country?.code?.toUpperCase();
  const minPickUpDate = todaySearchDate();
  const minDropOffDate = latestSearchDate(minPickUpDate, pickUpDate);
  const pickUpParams = pickUpPlace ? carPlaceSearchParams(pickUpPlace, 'pickUp') : {};
  const dropOffParams = dropOffPlace ? carPlaceSearchParams(dropOffPlace, 'dropOff') : {};
  const canSearch = Boolean(
    pickUpPlace &&
      Object.keys(pickUpParams).length > 0 &&
      country &&
      residence &&
      pickUpDate &&
      pickUpDate >= minPickUpDate &&
      dropOffDate &&
      dropOffDate >= minDropOffDate &&
      (!differentDropOff || dropOffPlace),
  );
  const pickUpUnsearchableReason = carPickUpUnsearchableReason({
    hasSelection: Boolean(pickUpPlace),
    hasCountry: Boolean(country),
  });
  const pickUpUnsearchable = pickUpUnsearchableReason !== 'ok';

  /**
   * El formulario tal como se escribiría en la URL (la búsqueda se confirma
   * escribiéndola ahí: entrada de historial). Es la ÚNICA derivación del
   * borrador — de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22), no dos expresiones parecidas.
   */
  const formParams: UrlParams = {
    buscar: '1',
    pickUpDate,
    pickUpTime,
    dropOffDate,
    dropOffTime,
    pais: country,
    residencia: residence,
    pago: paymentType || undefined,
    // El punto elegido viaja siempre como `plc_*`. Las keys del formato
    // NO elegido viajan en `undefined` a propósito: `url.commit` mergea, así
    // que hay que borrar lo que dejó una búsqueda o URL anterior (#2204).
    ...carLocationUrlParams(pickUpParams, 'pickUp'),
    ...carLocationUrlParams(differentDropOff ? dropOffParams : {}, 'dropOff'),
    // El nombre del punto no se busca: es para repintar el autocomplete.
    pickUpNombre: pickUpName,
    dropOffNombre: differentDropOff ? dropOffName : undefined,
  };
  const draftPayload = carSearchPayloadFromParams(cleanParams(formParams));
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);
  const selectionBlockedReason = useSearchSelectionBlockedReason(resultAuthority);
  const comparisonAuthority = useSearchResultAuthority(comparison, formDiverged);
  const comparisonBlockedReason = useSearchSelectionBlockedReason(comparisonAuthority);
  const comparisonCountdown = useFareCountdown(
    comparison.data ? comparison.dataUpdatedAt : undefined,
  );

  // La devolución en otro lugar se descarta al desmarcar el toggle.
  useEffect(() => {
    if (!differentDropOff) {
      setDropOffPlace(null);
    }
  }, [differentDropOff]);

  /**
   * Vacía la búsqueda: campos, filtros, URL y memoria de navegación. La
   * residencia NO se toca: es una preferencia del asesor (vive en
   * `localStorage`), no parte de esta búsqueda.
   */
  const handleClear = () => {
    setPickUpPlace(null);
    setDropOffPlace(null);
    setDifferentDropOff(false);
    setPickUpDate('');
    setPickUpTime('10:00');
    setDropOffDate('');
    setDropOffTime('10:00');
    setFilters(EMPTY_CAR_FILTERS);
    setComparisonRequest(null);
    url.reset();
  };

  const handleSearch = () => {
    if (!canSearch || !country || !pickUpName) return;
    localStorage.setItem(RESIDENCE_KEY, residence);
    setFilters(EMPTY_CAR_FILTERS);
    setComparisonRequest(null);
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('cars', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  const baseResults = (search.data?.results ?? []) as CarOffer[];
  const comparisonState = carRatePlanComparisonState({
    requested: comparisonIsForActiveSearch,
    fetching: comparison.isFetching,
    requestFailed: comparison.isError,
    providers: comparison.data?.providers,
  });
  const comparisonLoading = comparisonState === 'loading';
  const comparisonFailed = comparisonState === 'failed';
  const comparisonCompleted = comparisonState === 'completed';
  const expandedResults = (comparison.data?.results ?? []) as CarOffer[];
  const results = mergeCarRatePlanComparison(
    baseResults,
    expandedResults,
    comparisonCompleted,
  );
  const rateNames = comparisonCompleted ? carRatePlanNames(expandedResults) : [];
  const effectiveFilters = carFiltersForRatePlanComparison(filters, comparisonState);
  const hasComparableCars = baseResults.some(isCarRatePlanProvider);

  useEffect(() => {
    if (comparisonCompleted) return;
    setFilters((current) => current.rateNames.length > 0
      ? { ...current, rateNames: [] }
      : current);
  }, [comparisonCompleted]);

  const startRatePlanComparison = () => {
    if (!searchPayload || !activeSearchIdentity || !search.data) return;
    setComparisonRequest({
      baseIdentity: activeSearchIdentity,
      baseUpdatedAt: search.dataUpdatedAt,
      requestId: defaultAttemptIdFactory(),
      payload: carRatePlanComparisonPayload(searchPayload),
    });
  };
  /** Ofertas por proveedor: el strip dice quién contestó y con cuánto. */
  const countsByProvider = useMemo(
    () =>
      results.reduce<Record<string, number>>((acc, offer) => {
        acc[offer.provider] = (acc[offer.provider] ?? 0) + 1;
        return acc;
      }, {}),
    [results],
  );

  const companies = useMemo(
    () => [...new Set(results.map((offer) => offer.company ?? '').filter(Boolean))].sort(),
    [results],
  );
  // Se filtran las OFERTAS y después se agrupan por vehículo: el proveedor
  // cotiza el mismo auto una vez por plan de cobertura.
  const visible = useMemo(
    () => sortCarGroups(groupCarOffers(filterCars(results, effectiveFilters)), sort),
    [results, effectiveFilters, sort],
  );

  const days = searched
    ? rentalDays(searched.pickUpDate, searched.dropOffDate)
    : 1;

  /** Cuántos filtros hay puestos: en mobile es lo único visible del panel. */
  const activeFilterCount =
    filters.bodies.length +
    filters.companies.length +
    (comparisonCompleted ? filters.rateNames.length : 0) +
    (filters.minPassengers ? 1 : 0) +
    (filters.automaticOnly ? 1 : 0) +
    (filters.onTerminalOnly ? 1 : 0);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Devolver en otro lugar cambia QUÉ campos tiene la búsqueda, así que
          va sobre la barra y no mezclado entre los campos. */}
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={differentDropOff}
            onChange={(event) => setDifferentDropOff(event.target.checked)}
          />
        }
        label={
          <Typography variant="body2">
            {t('travelServices.cars.differentDropOff')}
          </Typography>
        }
      />

      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
      >
        <SearchField label={t('travelServices.cars.pickUp')} width={220} grow>
          <PlaceAutocomplete
            label={t('travelServices.cars.pickUp')}
            vertical="cars"
            value={pickUpPlace}
            onChange={setPickUpPlace}
            kinds={CAR_PLACE_KINDS}
            width="100%"
            required
          />
        </SearchField>
        {differentDropOff && <SearchDivider />}
        {differentDropOff && (
          <SearchField label={t('travelServices.cars.dropOff')} width={220} grow>
            <PlaceAutocomplete
              label={t('travelServices.cars.dropOff')}
              vertical="cars"
              value={dropOffPlace}
              onChange={setDropOffPlace}
              kinds={CAR_PLACE_KINDS}
              width="100%"
              required
            />
          </SearchField>
        )}
        <SearchDivider />
        <SearchField
          label={t('travelServices.cars.pickUpDate')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_WIDE_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.cars.pickUpDate')}
            value={pickUpDate}
            minDate={minPickUpDate}
            onChange={(nextDate) => {
              setPickUpDate(nextDate);
              // La devolución nunca puede quedar antes del retiro.
              setDropOffDate((current) => reconcileSearchEndDate(nextDate, current));
            }}
            onAccept={() => setDropOffDateOpen(true)}
            required
          />
        </SearchField>
        <SearchField
          label={t('travelServices.cars.pickUpTime')}
          width={96}
          xsShare={SEARCH_FIELD_NARROW_SHARE}
        >
          <TextField
            size="small"
            fullWidth
            type="time"
            label={t('travelServices.cars.pickUpTime')}
            value={pickUpTime}
            onChange={(event) => setPickUpTime(event.target.value)}
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.cars.dropOffDate')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_WIDE_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.cars.dropOffDate')}
            value={dropOffDate}
            onChange={setDropOffDate}
            minDate={minDropOffDate}
            open={dropOffDateOpen}
            onOpen={() => setDropOffDateOpen(true)}
            onClose={() => setDropOffDateOpen(false)}
            required
          />
        </SearchField>
        <SearchField
          label={t('travelServices.cars.dropOffTime')}
          width={96}
          xsShare={SEARCH_FIELD_NARROW_SHARE}
        >
          <TextField
            size="small"
            fullWidth
            type="time"
            label={t('travelServices.cars.dropOffTime')}
            value={dropOffTime}
            onChange={(event) => setDropOffTime(event.target.value)}
          />
        </SearchField>
        <SearchDivider />
        {/* País de residencia y forma de pago comparten fila también en
            mobile: son los dos campos "comerciales" de la búsqueda. */}
        <SearchField
          label={t('travelServices.cars.residence')}
          width={176}
          xsShare={SEARCH_FIELD_HALF_SHARE}
        >
          <Tooltip title={t('travelServices.cars.residenceHint')}>
            <Autocomplete
              size="small"
              fullWidth
              options={residenceOptions}
              value={residenceOptions.find((option) => option.code === residence) ?? null}
              onChange={(_event, option) => setResidence(option?.code ?? '')}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('travelServices.cars.residence')}
                  required
                />
              )}
            />
          </Tooltip>
        </SearchField>
        <SearchField
          label={t('travelServices.cars.paymentType')}
          width={150}
          xsShare={SEARCH_FIELD_HALF_SHARE}
        >
          <TextField
            select
            size="small"
            fullWidth
            label={t('travelServices.cars.paymentType')}
            value={paymentType}
            onChange={(event) => setPaymentType(event.target.value as '' | 'ppd' | 'pod')}
          >
            <MenuItem value="ppd">{t('travelServices.cars.paymentPpd')}</MenuItem>
            <MenuItem value="pod">{t('travelServices.cars.paymentPod')}</MenuItem>
            <MenuItem value="">{t('travelServices.cars.paymentAny')}</MenuItem>
          </TextField>
        </SearchField>
      </SearchBar>

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="cars"
          describe={(params) => describeCars(params, t, i18n.language)}
        />
      )}

      {/* Mensaje DISTINTO por causa (`carPickUpUnsearchableReason`): en
          PLACES el picker siempre manda código, así que "sin código ni
          coordenadas" sería falso y mandaría a un remedio que no aplica
          (el picker ya está limitado a ciudad/aeropuerto) — ahí lo que
          falta es el `country` del lugar elegido. */}
      {pickUpUnsearchable && (
        <Alert severity="warning">
          {t(
            pickUpUnsearchableReason === 'placesNoCountry'
              ? 'travelServices.cars.unsearchableLocationNoCountry'
              : 'travelServices.cars.unsearchableLocation',
          )}
        </Alert>
      )}
      {invalidCommittedDropOff && (
        <Alert severity="warning">
          {t('travelServices.cars.reselectCanonicalDropOff')}
        </Alert>
      )}
      {invalidCommittedPickUp && (
        <Alert severity="warning">
          {t('travelServices.cars.reselectCanonicalPickUp')}
        </Alert>
      )}

      {search.data && !search.isLoading && results.length > 0 && (
        <ResultFiltersBar
          activeCount={activeFilterCount}
          onClear={() => setFilters(EMPTY_CAR_FILTERS)}
          resultCount={visible.length}
          sort={{
            value: sort,
            onChange: (value) => setSort(value as CarSort),
            options: [
              { value: 'price', label: t('travelServices.cars.sortPrice') },
              { value: 'passengers', label: t('travelServices.cars.sortPassengers') },
              { value: 'company', label: t('travelServices.cars.sortCompany') },
            ],
          }}
        >
          {(stacked) => (
            <FilterGroups
              filters={effectiveFilters}
              setFilters={setFilters}
              companies={companies}
              rateNames={rateNames}
              offers={results}
              stacked={stacked}
            />
          )}
        </ResultFiltersBar>
      )}

      {/* La URL pide una búsqueda que no se puede ejecutar (link
          recortado o editado a mano): decirlo, no quedarse mudo. */}
      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describeCars(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {search.data && !search.isLoading && hasComparableCars && (
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
          <Button
            variant="outlined"
            onClick={startRatePlanComparison}
            disabled={comparisonLoading || formDiverged}
            startIcon={comparisonLoading ? <CircularProgress size={16} /> : undefined}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {comparisonLoading
              ? t('travelServices.cars.comparingProtections')
              : comparisonCompleted
                ? t('travelServices.cars.refreshProtections')
                : comparisonFailed
                  ? t('travelServices.cars.retryProtections')
                  : t('travelServices.cars.compareProtections')}
          </Button>
          {comparisonFailed && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              {t('travelServices.cars.protectionComparisonFailed')}
            </Alert>
          )}
          {comparisonCompleted && comparisonAuthority.isLocallyExpired && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              {t('travelServices.cars.protectionComparisonExpired')}
            </Alert>
          )}
          {comparisonCompleted && !comparisonAuthority.isLocallyExpired && (
            <Typography variant="caption" color="text.secondary">
              {rateNames.length > 0
                ? t('travelServices.cars.protectionComparisonReady', {
                  count: rateNames.length,
                  time: comparisonCountdown.label,
                })
                : t('travelServices.cars.protectionComparisonEmpty')}
            </Typography>
          )}
        </Box>
      )}

      {/* El recorrido de la búsqueda comprometida, uno por búsqueda y
          colapsado: el iframe no existe hasta que el asesor lo abre. La
          identidad de la búsqueda va de `key` para que una búsqueda nueva
          arranque cerrada y sin el mapa de la anterior. */}
      {url.params.buscar === '1' && searchPayload && (
        <SearchRouteMap
          key={searchRouteMapKey(activeSearchIdentity, bookingRoutePoints)}
          vertical="cars"
          points={bookingRoutePoints}
        />
      )}

      {search.isLoading && <ResultsSkeleton archetype="product" />}
      {search.isError && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}
      {search.data && !search.isLoading && (
        <ProviderStatusStrip
          providers={search.data.providers}
          countsByProvider={countsByProvider}
        />
      )}
      {!search.data && !search.isLoading && !search.isError && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.cars.emptyState')}
        </Typography>
      )}

      {search.data && !search.isLoading && (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('travelServices.cars.found', {
              count: visible.length,
              rates: visible.reduce((total, group) => total + group.rates.length, 0),
            })}
          </Typography>
          {visible.length === 0 ? (
            <Alert severity="info">{t('travelServices.noResults')}</Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {visible.map((group, index) => (
                <CarOfferCard
                  key={group.key}
                  group={group}
                  index={index}
                  days={days}
                  bookingDisabledReasonForOffer={(offer) =>
                    comparisonCompleted && isCarRatePlanProvider(offer)
                      ? comparisonBlockedReason
                      : selectionBlockedReason
                  }
                  onBook={(offer) => {
                    const expanded = comparisonCompleted && isCarRatePlanProvider(offer);
                    setBookingQuote(
                      captureSelectableSearchQuote(
                        offer,
                        expanded ? comparison.dataUpdatedAt : search.dataUpdatedAt,
                        'cars',
                        CONFIG.AGENCY_ID,
                        searchPayload,
                        Date.now(),
                        formDiverged,
                      ),
                    );
                  }}
                />
              ))}
            </Box>
          )}
        </>
      )}

      <BookingModal
        vertical="cars"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        routePoints={bookingRoutePoints}
        party={committedParty({ vertical: 'cars', payload: searchPayload })}
        carSummary={bookingOffer ? {
          kind: 'car',
          offer: bookingOffer,
          pickUp: {
            date: formatDate(searched?.pickUpDate),
            time: searched?.pickUpTime ?? '',
          },
          dropOff: {
            date: formatDate(searched?.dropOffDate),
            time: searched?.dropOffTime ?? '',
          },
          days,
          ...(searched?.pickUpLocation
            ? { locationName: shortLocationName(searched.pickUpLocation) }
            : {}),
        } : undefined}
        onClose={() => setBookingQuote(null)}
      />
    </Box>
  );
}

/** "2026-09-15" → "15/09/2026" (fecha suelta, sin zona horaria). */
function formatDate(value?: string): string {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return day ? `${day}/${month}/${year}` : value;
}
